import { format } from 'date-fns';
import { Icon, Row } from '~/commonComponents';
import Button from '~/commonComponents/Button';
import { OverlappingJobPopup } from '~/commonComponents/popups/OverlappingJobPopup/OverlappingJobPopup';
import { GuideLinesSwitch } from '~/components/Request/multiday/components/Guidelines/GuidelinesSwitch';
import { ShowChildMissingTermsPopup } from '~/components/Request/multiday/components/ShowChildMissingTermsPopup';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import WarningIcon from '~/images/icons/Warning';
import { Colors } from '~/themes/colors';
import { ShiftRequestHeader } from '../common';
import { ShiftReviewCard } from './ShiftReviewCard';
import { useReviewConfirm } from './useReviewConfirm';

export const ReviewConfirmScreen = () => {
  const {
    sortedDates,
    professionType,
    preferredSpecialty,
    arrivalTime,
    departureTime,
    mealBreakDuration,
    isMealBreakPaid,
    rate,
    isAssistedHygiene,
    shiftOverrides,

    dateToDelete,
    acceptedGuidelines,
    showNavigationGuard,
    showOvertimeLimitPopup,
    showLeavingPopup,
    isPostingShifts,
    isOverlappingJob,
    showJobCreatePaymentError,

    setDateToDelete,
    acceptedGuidelinesControls,
    leavingPopupControls,
    navigationGuardControls,
    overtimeLimitPopupControls,

    handleDeleteDate,
    handleBack,
    handleEditShift,
    handleSendRequest,
    handleConfirmSubmission,
    cancelRequest,

    clearPendingNavigation,
    navigateToPending,

    confirmOverlapping,
    goBackOverlapping,

    hidePaymentError,
    updatePayment,
  } = useReviewConfirm();

  return (
    <ShiftRequestHeader currentStep={2} onBack={handleBack} maxWidth={1120}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, 438px)',
          gap: 24,
          justifyContent: 'center',
          marginTop: 48,
          marginBottom: 60,
          width: '100%',
        }}
      >
        {sortedDates.map(({ date, originalIndex }) => {
          const dateKey = date.toISOString();
          const override = shiftOverrides?.[dateKey];
          return (
            <ShiftReviewCard
              key={dateKey}
              date={date}
              professionType={professionType}
              specialty={override?.preferredSpecialty ?? preferredSpecialty}
              arrivalTime={override?.arrivalTime ?? arrivalTime}
              departureTime={override?.departureTime ?? departureTime}
              mealBreakDuration={override?.mealBreakDuration ?? mealBreakDuration}
              isMealBreakPaid={override?.isMealBreakPaid ?? isMealBreakPaid}
              rate={(override?.rate ?? rate) ?? 0}
              isAssistedHygiene={override?.isAssistedHygiene ?? isAssistedHygiene}
              onEdit={() => handleEditShift(originalIndex)}
              onDelete={() => setDateToDelete(date)}
            />
          );
        })}
      </div>

      <GuideLinesSwitch onSwitch={acceptedGuidelinesControls.set} />

      <Row gap={16} justifyContent="center">
        <Button
          text="Cancel Request"
          data-testid="cancel-request-button"
          onClick={leavingPopupControls.setOn}
          style={{
            backgroundColor: Colors.secondary_500,
          }}
        />
        <Button
          text={`Post ${sortedDates.length} ${sortedDates.length === 1 ? 'Shift' : 'Shifts'}`}
          data-testid="confirm-shifts-button"
          disabled={!acceptedGuidelines || isPostingShifts}
          isLoading={isPostingShifts}
          onClick={handleSendRequest}
          style={{
            backgroundColor:
              acceptedGuidelines && !isPostingShifts ? Colors.primary_500 : Colors.primary_200,
          }}
        />
      </Row>

      {dateToDelete && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Delete Shift"
          description={
            <>
              Are you sure you would like to delete the shift for{'\n'}
              <strong>{format(dateToDelete, 'EEE, MMM dd, yyyy')}</strong>?
            </>
          }
          leftButtonText="Go Back"
          leftButtonAction={() => setDateToDelete(null)}
          rightButtonText="Delete"
          rightButtonAction={handleDeleteDate}
          closePopup={() => setDateToDelete(null)}
          dataTestId="delete-shift-popup"
        />
      )}

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            navigationGuardControls.setOff();
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            navigationGuardControls.setOff();
          }}
          dataTestId="navigation-guard-popup"
        />
      )}

      {showOvertimeLimitPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} color={Colors.warning_500} />
            </div>
          )}
          title="Overtime Hours"
          description="The payable hours of this shift exceed your state's daily overtime limit. Your office will be subject to additional charges for any overtime hours worked by a professional."
          leftButtonText="Go Back"
          leftButtonAction={overtimeLimitPopupControls.setOff}
          rightButtonText="Continue"
          rightButtonAction={() => {
            overtimeLimitPopupControls.setOff();
            handleConfirmSubmission();
          }}
          closePopup={overtimeLimitPopupControls.setOff}
          dataTestId="overtime-limit-popup"
        />
      )}

      {showLeavingPopup && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} />
            </div>
          )}
          title="Please Confirm"
          description="Are you sure you would like to cancel this request?"
          leftButtonText="Go Back"
          leftButtonStyle={{ backgroundColor: Colors.secondary_500 }}
          leftButtonAction={leavingPopupControls.setOff}
          rightButtonText="Confirm"
          rightButtonAction={cancelRequest}
          closePopup={leavingPopupControls.setOff}
          dataTestId="cancel-request-popup"
        />
      )}

      {showJobCreatePaymentError && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} />
            </div>
          )}
          title="Auto-Confirm Disabled"
          description={
            <>
              The auto-confirm feature is only available for offices that have input their payment
              information.
              <br />
              <br />
              You can update your payment information now or continue without payment, and with
              auto-confirm disabled.
            </>
          }
          leftButtonText="Go Back"
          leftButtonStyle={{ backgroundColor: Colors.secondary_500 }}
          leftButtonAction={hidePaymentError}
          rightButtonText="Update"
          rightButtonAction={updatePayment}
          closePopup={hidePaymentError}
          dataTestId="payment-error-popup"
        />
      )}

      <OverlappingJobPopup
        isOpen={!!isOverlappingJob}
        onConfirm={confirmOverlapping}
        onGoBack={goBackOverlapping}
      />

      <ShowChildMissingTermsPopup />
    </ShiftRequestHeader>
  );
};
